import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssetObligationsResponse, ObligationRecordCreate, ObligationStatus } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { queue } from "../../offline/queue";
import { useQueueState } from "../../offline/state";
import { useBuildingCache } from "../building/useBuilding";

// assetId null = onderhoud en keuringen bij het gebouw zelf, zonder asset.
export const obligationsKey = (buildingId: string, assetId: string | null) =>
  ["obligations", assetId ?? `b:${buildingId}`] as const;
export const obligationsPath = (buildingId: string, assetId: string | null) =>
  assetId ? `/assets/${assetId}/obligations` : `/buildings/${buildingId}/building-obligations`;

/** Verplichtingen, keuringen en voorstellen van één asset, of van het gebouw zelf (`assetId` null). */
export function useAssetObligations(buildingId: string, assetId: string | null, enabled = true) {
  return useQuery({
    queryKey: obligationsKey(buildingId, assetId),
    queryFn: () => api<AssetObligationsResponse>(obligationsPath(buildingId, assetId)),
    enabled,
  });
}

/** Na een wijziging: antwoord in de cache en de status van de asset in het gebouw bijwerken. */
export function useObligationsCache(buildingId: string, assetId: string | null) {
  const qc = useQueryClient();
  const cache = useBuildingCache(buildingId);
  return (r: AssetObligationsResponse) => {
    qc.setQueryData(obligationsKey(buildingId, assetId), r);
    if (assetId) cache.patchAsset(assetId, { obligationStatus: r.status });
    // Afkeur maakt een bevinding, een nieuwe keuring sluit de taak *Keuring plannen*.
    for (const key of ["obligation-list", "issues", "tasks", "my-tasks"])
      void qc.invalidateQueries({ queryKey: [key] });
    if (assetId) void qc.invalidateQueries({ queryKey: ["asset", assetId] });
  };
}

/** Keuringen die op dit apparaat wachten op verbinding (offline vastgelegd). */
export function usePendingRecords(obligationIds: string[]) {
  useQueueState();
  const ids = new Set(obligationIds);
  return queue
    .mine()
    .filter((op) => op.req.kind === "http" && /^\/asset-obligations\/[^/]+\/records$/.test(op.req.path))
    .map((op) => {
      const r = op.req as { path: string; body?: unknown };
      return { obligationId: r.path.split("/")[2]!, body: r.body as ObligationRecordCreate };
    })
    .filter((p) => ids.has(p.obligationId));
}

/**
 * Keuring vastleggen via de offline-wachtrij: online meteen verwerkt, offline later verstuurd
 * (idempotent op het record-ID).
 */
export async function sendRecord(
  buildingId: string,
  label: string,
  obligationId: string,
  body: ObligationRecordCreate,
) {
  return queue.enqueue<AssetObligationsResponse>({
    id: body.id,
    buildingId,
    label,
    req: { kind: "http", method: "POST", path: `/asset-obligations/${obligationId}/records`, body },
  });
}

export const STATUS_ORDER: ObligationStatus[] = ["overdue", "soon", "unknown", "ok", "na"];
